import { useState, type FormEvent } from "react";
import { ArrowRight, LockKey, User, WarningCircle } from "@phosphor-icons/react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import brandMarkUrl from "../../../../xmirror-mark.png";
import { ApiError } from "../lib/api";
import { useAuth } from "../app/auth";

const errorMessages: Record<string, string> = {
  invalid_credentials: "账号或密码不正确。",
  invalid_refresh_token: "当前会话已失效，请重新登录。",
};

export function LoginPage() {
  const { login, status } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState("admin");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (status === "authenticated") {
    return <Navigate to="/overview" replace />;
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await login({ username: username.trim(), password });
      const next =
        new URLSearchParams(location.search).get("next") || "/overview";
      navigate(next, { replace: true });
    } catch (reason) {
      const code = reason instanceof ApiError ? reason.message : "";
      setError(errorMessages[code] || "登录失败，请稍后重试。");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="login-page">
      <section className="login-brand">
        <div className="login-brand-lockup">
          <img src={brandMarkUrl} alt="XMirror" />
          <strong>XMirror Connect</strong>
        </div>
        <div>
          <h1>连接每一台工作手机，<br />让运营始终清晰可控。</h1>
          <p>
            管理设备、任务、合规和告警，通过统一控制中心完成从发现问题到审计追踪的完整闭环。
          </p>
        </div>
        <small>企业设备与渠道连接控制中心</small>
      </section>
      <section className="login-panel">
        <form onSubmit={submit}>
          <header>
            <h2>登录管理控制台</h2>
            <p>输入账号和密码，系统将自动识别所属租户。</p>
          </header>
          {error && (
            <div className="login-error" role="alert">
              <WarningCircle size={18} />
              {error}
            </div>
          )}
          <label>
            <span>账号</span>
            <div>
              <User size={18} />
              <input
                required
                autoComplete="username"
                value={username}
                onChange={(event) => setUsername(event.target.value)}
              />
            </div>
          </label>
          <label>
            <span>密码</span>
            <div>
              <LockKey size={18} />
              <input
                required
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </div>
          </label>
          <button className="primary-button login-submit" disabled={submitting}>
            {submitting ? "正在登录…" : "进入控制台"}
            {!submitting && <ArrowRight size={18} />}
          </button>
          <p className="login-security">登录和敏感操作将写入合规审计日志。</p>
        </form>
      </section>
    </main>
  );
}
